// All copy on the landing page. Source of truth for claims: docs/portfolio-tech-inventory.md
// (evidence-based survey of the portfolio, 2026-08-24). Keep numbers citable.

export type TermLine = { mark: string; color: string; agent: string; agentColor: string; text: string };

export const TERM_SCRIPT: TermLine[] = [
  { mark: '$', color: '#fbbf24', agent: 'hermes', agentColor: '#fbbf24', text: 'spawn --team exonstech --pipeline full' },
  { mark: '✓', color: '#34d399', agent: 'claude-code', agentColor: '#f59e0b', text: 'prompt → graph → backend · 42 files · 0 errors' },
  { mark: '✓', color: '#34d399', agent: 'codex', agentColor: '#f5b849', text: 'check:gates · 38/38 gates · 1612 tests passing' },
  { mark: '⠿', color: '#f5b849', agent: 'openclaw', agentColor: '#a78bfa', text: 'wiring MCP tools + 259-connector catalogue' },
  { mark: '✓', color: '#34d399', agent: 'hermes', agentColor: '#fbbf24', text: 'human review requested → approved' },
  { mark: '▸', color: '#f59e0b', agent: 'deploy', agentColor: '#34d399', text: 'k8s rollout (gVisor) · lat 41ms · healthy' },
  { mark: '✓', color: '#34d399', agent: 'claude-code', agentColor: '#f59e0b', text: 'rag eval: 0.0% hallucination · 0/10 injections' },
  { mark: '⠿', color: '#f5b849', agent: 'codex', agentColor: '#f5b849', text: 'pgvector index rebuilt · hybrid BM25 + RRF' },
  { mark: '✓', color: '#34d399', agent: 'openclaw', agentColor: '#a78bfa', text: 'unsloth fine-tune: +7.2% on domain eval · LoRA merged' },
  { mark: '▸', color: '#f59e0b', agent: 'hermes', agentColor: '#fbbf24', text: 'next task queued: client onboarding flow' },
];

export const STATS = [
  { value: '20+', label: 'YEARS FOR CLIENTS' },
  { value: '12', label: 'OWN PLATFORMS' },
  { value: '04', label: 'LIVE IN PRODUCTION' },
  { value: '06', label: 'PRACTICE AREAS' },
];

export const MARQUEE = [
  'CLAUDE CODE', 'CODEX', 'HERMES AGENTS', 'OPENCLAW', 'MCP', 'UNSLOTH', 'LANGGRAPH', 'CREWAI', 'AUTOGEN',
  'N8N', 'LANGFLOW', 'PYDANTIC-AI', 'PGVECTOR', 'OLLAMA', 'GPT-OSS 120B', 'QWEN3.5', 'DEEPSEEK-V4',
  'CLAUDE OPUS 4.7', 'YOLO11', 'SDXL', 'ELEVENLABS', 'INSIGHTFACE', 'DOCLING', 'POSTGRES RLS',
  'KUBERNETES + GVISOR', 'AWS · GCP · AZURE', 'LAMBDA LABS', 'STRIPE',
];

export type Capability = { num: string; tag: string; title: string; items: string[] };

export const PLATFORM: Capability[] = [
  {
    num: '/01', tag: 'AGENTS', title: 'Agentic Systems & Multi-Agent Runtimes',
    items: [
      'Custom agent loops — or LangGraph, AutoGen, CrewAI, LangChain when they fit',
      'Deterministic role routing · per-state tool allowlists',
      'MCP servers & graded MCP client allowlists',
      'Low/no-code orchestration — n8n, Langflow',
      'Schema-typed outputs with validate-and-repair',
    ],
  },
  {
    num: '/02', tag: 'GROUNDED RAG', title: 'Grounded, Evaluated Retrieval',
    items: [
      'Hybrid vector + BM25 · RRF · cross-encoder rerank',
      'Citation verification (NLI) · groundedness checks',
      'Eval gates: hallucination ≤2% · injection-resistant',
      'Postgres RLS multi-tenancy · CI isolation gate',
    ],
  },
  {
    num: '/03', tag: 'MODELS + INFERENCE', title: 'Fine-Tuning, Self-Hosted & Frontier Inference',
    items: [
      'Fine-tuning open LLMs & VLMs with Unsloth — LoRA / QLoRA, evals, merges',
      'Self-hosted gateway — gpt-oss 120B, Qwen3.5, DeepSeek-V4, GLM-5.2',
      'Claude Opus 4.7 / Sonnet 4.6 / Haiku 4.5 · GPT-4.1',
      'Multi-model fallback chains · cross-family judging',
      'Cloud GPU/TPU — AWS, GCP, Azure, Lambda Labs · on-prem RTX 4090',
    ],
  },
  {
    num: '/04', tag: 'VISION', title: 'Computer Vision & Media Pipelines',
    items: [
      'YOLO detection & segmentation — custom-trained on your data',
      'SigLIP-2 · InsightFace · RT-DETR · Depth-Anything-V2 · VLMs',
      'Docling + Tesseract OCR for messy real-world documents',
      'ffmpeg highlight reels · Celery job systems',
      'Licence-clean model selection with written BOMs',
    ],
  },
  {
    num: '/05', tag: 'PLATFORM', title: 'Product Engineering & Infrastructure',
    items: [
      'FastAPI · Fastify · React 19 · Postgres + pgvector',
      'Docker · Kubernetes + gVisor · Helm · Terraform · GitHub Actions',
      'Stripe · Resend · Sentry · RLS · MFA · hash-chained audit logs',
      'iOS & Android — BioSteel Teams',
    ],
  },
  {
    num: '/06', tag: 'GENERATIVE MEDIA · R&D', title: 'Generative Media & 3D',
    items: [
      'Text-to-image / video / music — SDXL, Midjourney, DALL·E',
      'Voice — ElevenLabs, Higgs Audio v3 cloning · lipsync',
      'Image → 3D and rigged characters · Unreal Engine 5.8 + MetaHuman',
      'Video → performance capture: GVHMR, WHAM, MediaPipe',
    ],
  },
];

export type WorkKind = 'client' | 'own';

export type Product = {
  index: string;
  name: string;
  /** Client engagement vs. a platform we own — the badge colour follows this. */
  kind: WorkKind;
  status: string;
  taglineLead: string;
  taglineAccent: string;
  description: string;
  tags: string[];
  url?: string;
  urlLabel?: string;
  /** Single screenshot filling the media cell (object-fit: cover). */
  image?: string;
  /** Several panels laid edge-to-edge as one continuous strip. */
  images?: string[];
  imageAlt: string;
  layout: 'textFirst' | 'imageFirst';
};

export const PRODUCTS: Product[] = [
  {
    index: '01', name: 'BioSteel Teams', kind: 'client', status: 'SHIPPED', layout: 'imageFirst',
    taglineLead: 'One app for the', taglineAccent: 'whole team.',
    description: 'Designed and built by eXonstech for BioSteel, on iOS and Android. A platform for athletes, coaches and families to connect, organize and manage their sports journey — live scoring, event management and real-time team updates, engineered for performance and scale.',
    tags: ['iOS', 'Android', 'Live scoring', 'Team scheduling'],
    images: ['/images/bt-1.webp', '/images/bt-2.webp', '/images/bt-3.webp', '/images/bt-4.webp'],
    imageAlt: 'BioSteel Teams app — dashboard, team scheduling, events and chat',
  },
  {
    index: '02', name: 'AutoArchitect', kind: 'own', status: 'LIVE', layout: 'textFirst',
    taglineLead: 'Describe it.', taglineAccent: 'Watch it build.',
    description: 'A prompt becomes typed requirements, one semantic graph, then a materialized full-stack app — backend, workflows, approvals, connectors and an AI agent team. Tests, seeds, guards and UI are all projections of that one graph. A bounded six-role agent roster with deterministic routing does the work inside a gVisor-isolated runtime. Any domain, no templates.',
    tags: ['Open-core · MIT', 'BYO LLM', '259-connector catalogue', '1612 tests · 38 gates', 'K8s + gVisor'],
    url: 'https://autoarchitect.ai', urlLabel: 'autoarchitect.ai ↗',
    image: '/images/autoarchitect-shot.webp', imageAlt: 'AutoArchitect landing page',
  },
  {
    index: '03', name: 'Lumha', kind: 'own', status: 'LIVE', layout: 'imageFirst',
    taglineLead: 'Where creators become', taglineAccent: 'influencers.',
    description: 'The AI-first course platform for creators. Create content with AI — short video, audio, courses — publish everywhere, and monetize every way: products, memberships, tips, coaching and brand deals, all on a branded site. First full draft in minutes, zero lines of code.',
    tags: ['AI course studio', '9 archetypes', 'Whisper ASR + TTS', 'Stripe', 'No-code'],
    url: 'https://lumha.com', urlLabel: 'lumha.com ↗',
    image: '/images/lumha-shot.webp', imageAlt: 'Lumha landing page',
  },
];

export type Stage = 'client' | 'production' | 'beta' | 'lab';

export type MorePlatform = { name: string; stage: Stage; stageLabel: string; blurb: string; tags: string[] };

export const MORE_PLATFORMS: MorePlatform[] = [
  {
    name: 'LensEvents AI', stage: 'production', stageLabel: 'PRODUCTION',
    blurb: 'Face-aware event media: recognition, clustering, per-guest privacy controls and an AI creative director.',
    tags: ['InsightFace', 'pgvector', 'Celery', 'AWS'],
  },
  {
    name: 'Penny · AI Portal', stage: 'beta', stageLabel: 'BETA',
    blurb: 'Multi-tenant grounded RAG agents for law firms — citation-verified answers with published hallucination gates.',
    tags: ['PydanticAI', 'Docling', 'RLS', 'Eval gates'],
  },
  {
    name: 'CareerBeyond AI', stage: 'beta', stageLabel: 'BETA',
    blurb: 'Résumé → ATS score → cover letter → branded PDF, with persona mentor agents and mock interviews.',
    tags: ['MCP tools', 'LinkedIn', 'WYSIWYG builder'],
  },
  {
    name: 'Klipki AI', stage: 'beta', stageLabel: 'BETA',
    blurb: 'Game footage → highlight reels, stats and platform-tuned captions across six sports.',
    tags: ['ffmpeg', 'Vision LLM', 'Celery'],
  },
  {
    name: 'Zakhu', stage: 'lab', stageLabel: 'PRE-LAUNCH',
    blurb: 'Brokerage OS: an agent swarm for real-estate agents with scope-enforced retrieval and a priced lead ledger.',
    tags: ['Agent swarm', 'MLS / CREA', 'Stripe'],
  },
  {
    name: 'aiframework', stage: 'lab', stageLabel: 'LAB',
    blurb: 'Private, self-hostable AI workspace for professional-services firms with versioned vertical packs.',
    tags: ['Vertical packs', 'Licence server', 'Air-gapped'],
  },
  {
    name: 'Fabric3D', stage: 'lab', stageLabel: 'LAB',
    blurb: 'Text or photo → engine-ready 3D assets and rigged characters, verified by a machine-checkable conformance contract.',
    tags: ['TRELLIS.2', 'Hunyuan3D', 'UniRig', 'MCP'],
  },
  {
    name: 'Wujud Studio', stage: 'lab', stageLabel: 'LAB',
    blurb: 'Generative media R&D: video → character performance, voice cloning, and premise → playable game.',
    tags: ['MetaHuman', 'GVHMR', 'Higgs Audio', 'UE5 C++'],
  },
];

export const TOOLKIT = [
  'Agentic AI & MCP', 'Fine-Tuning with Unsloth', 'Grounded RAG & Evals', 'Self-Hosted LLM Serving',
  'Cloud GPU & MLOps', 'Computer Vision — YOLO & VLMs', 'Document Intelligence & OCR',
  'Voice & Speech — ASR, TTS, Cloning', 'Text-to-Image / Video / Music', 'Workflow Automation — n8n, Langflow',
  'Enterprise Integration & APIs', 'Multi-Tenant SaaS & Security',
];

export const CONTACT = {
  email: 'info@exonstech.com',
  phoneDisplay: '+1 (647) 907-9450',
  phoneHref: 'tel:+16479079450',
};
